import { RegisterClass } from "core/Misc/typeStore";
import { PropertyTypeForEdition, editableInPropertyPage } from "core/Decorators/nodeDecorator";
import { Scalar } from "core/Maths/math.scalar";
import { Epsilon } from "core/Maths/math.constants";
import { RewardGraphNode } from "../rewardGraphNode";
import { RewardGraphConnectionPointTypes } from "../rewardGraphConnectionPointTypes";
import { RewardGraphConnectionPoint } from "../rewardGraphConnectionPoint";
import { RewardGraphBuildState } from "../rewardGraphBuildState";

/**
 * Conditions supported by the condition block
 */
export enum RewardConditionBlockTests {
    /** Equal */
    Equal,
    /** NotEqual */
    NotEqual,
    /** LessThan */
    LessThan,
    /** GreaterThan */
    GreaterThan,
    /** LessOrEqual */
    LessOrEqual,
    /** GreaterOrEqual */
    GreaterOrEqual
}

function OperationString(operation : RewardConditionBlockTests) : string {
    if( typeof operation === 'number' )
        return RewardConditionBlockTests[operation];
    return operation as string;
}

function StringOperation(op : string) {
    return (RewardConditionBlockTests as any)[op];
}

/**
 * Block used to evaluate a condition and return a true or false value
 */
export class RewardConditionBlock extends RewardGraphNode {
    /**
     * Gets or sets the test used by the block
     */
    @editableInPropertyPage("Test", PropertyTypeForEdition.List, "ADVANCED", {
        notifiers: { rebuild: true },
        options: [
            { label: "Equal", value: RewardConditionBlockTests.Equal },
            { label: "NotEqual", value: RewardConditionBlockTests.NotEqual },
            { label: "LessThan", value: RewardConditionBlockTests.LessThan },
            { label: "GreaterThan", value: RewardConditionBlockTests.GreaterThan },
            { label: "LessOrEqual", value: RewardConditionBlockTests.LessOrEqual },
            { label: "GreaterOrEqual", value: RewardConditionBlockTests.GreaterOrEqual },
        ],
    })
    public test = RewardConditionBlockTests.Equal;

    /**
     * Create a new ConditionBlock
     * @param name defines the block name
     */
    public constructor(name: string) {
        super(name);

        this.registerInput("left", RewardGraphConnectionPointTypes.Float);
        this.registerInput("right", RewardGraphConnectionPointTypes.Float, true, 0);
        this.registerInput("ifTrue", RewardGraphConnectionPointTypes.AutoDetect, true, 1);
        this.registerInput("ifFalse", RewardGraphConnectionPointTypes.AutoDetect, true, 0);

        this.registerOutput("output", RewardGraphConnectionPointTypes.BasedOnInput);

        this._outputs[0]._typeConnectionSource = this._inputs[2];
        this._outputs[0]._defaultConnectionPointType = RewardGraphConnectionPointTypes.Boolean;
        this._linkConnectionTypes(2, 3);
    }

    /**
     * Gets the current class name
     * @returns the class name
     */
    public override getClassName() {
        return "RewardConditionBlock";
    }

    /**
     * Gets the left input component
     */
    public get left(): RewardGraphConnectionPoint {
        return this._inputs[0];
    }

    /**
     * Gets the right input component
     */
    public get right(): RewardGraphConnectionPoint {
        return this._inputs[1];
    }

    /**
     * Gets the ifTrue input component
     */
    public get ifTrue(): RewardGraphConnectionPoint {
        return this._inputs[2];
    }

    /**
     * Gets the ifFalse input component
     */
    public get ifFalse(): RewardGraphConnectionPoint {
        return this._inputs[3];
    }

    /**
     * Gets the output component
     */
    public get output(): RewardGraphConnectionPoint {
        return this._outputs[0];
    }

    protected override _buildBlock() {
        if (!this.left.isConnected) {
            this.output._storedFunction = null;
            this.output._storedValue = null;
            return;
        }

        const func = (state: RewardGraphBuildState) => {
            const left = this.left.getConnectedValue(state) as number;
            const right = this.right.getConnectedValue(state) as number;
            let condition = false;

            switch (this.test) {
                case RewardConditionBlockTests.Equal:
                    condition = Scalar.WithinEpsilon(left, right, Epsilon);
                    break;
                case RewardConditionBlockTests.NotEqual:
                    condition = left !== right;
                    break;
                case RewardConditionBlockTests.LessThan:
                    condition = left < right;
                    break;
                case RewardConditionBlockTests.GreaterThan:
                    condition = left > right;
                    break;
                case RewardConditionBlockTests.LessOrEqual:
                    condition = left <= right;
                    break;
                case RewardConditionBlockTests.GreaterOrEqual:
                    condition = left >= right;
                    break;
            }
            return condition;
        };

        this.output._storedFunction = (state) => {
            if (func(state)) {
                return this.ifTrue.getConnectedValue(state);
            }

            return this.ifFalse.getConnectedValue(state);
        };
    }

    protected override _dumpPropertiesCode() {
        const codeString = super._dumpPropertiesCode() + `${this._codeVariableName}.test = MELS.RewardConditionBlock.${RewardConditionBlockTests[this.test]};\n`;
        return codeString;
    }

    /**
     * Serializes this block in a JSON representation
     * @returns the serialized block object
     */
    public override serialize(): any {
        const serializationObject = super.serialize();

        serializationObject.test = OperationString(this.test);

        return serializationObject;
    }

    public override _deserialize(serializationObject: any) {
        super._deserialize(serializationObject);

        this.test = StringOperation(serializationObject.test);
    }
}

RegisterClass("MELS.RewardConditionBlock", RewardConditionBlock);
